import React from 'react';
import { Field, useFormikContext } from 'formik';
import { MiniAppFocusWrapper } from 'src/components';
import SpinEditReskin from 'src/components/spin-edit-reskin/spin-edit-reskin';
import { InputField, isVirtualDevice, PriceType } from 'src/features/common';
import { CartItem } from 'src/features/common/types/application/cart';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import {
  AddressModalPageProps,
  Delivery,
} from 'src/features/dpo/components/delivery-address-modal-v2/delivery-address-modal';
import {
  getProductName,
  getProductNamesInCart,
} from 'src/features/dpo/helpers/tealium-service-helpers';
import { useApplication } from 'src/hooks/useApplication';
import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect';

// const findAssignedAddress = (deliveries: AppDelivery[], productId: string): string | undefined => {
//   const delivery = deliveries.find((delivery) => delivery.productId === productId);
//   return delivery ? delivery.addressId : undefined;
// };
export function AllocateDevicesStep(props: AddressModalPageProps) {
  const { application, dpoTealiumService } = useApplication();
  const [currentQuantity, setCurrentQuantity] = React.useState<number | undefined>(
    props.currentQuantity,
  );

  const { values } = useFormikContext<Delivery>();

  // // if the device is assigned to an address, that address shouldn't be displayed
  // const assignedAddressId = findAssignedAddress(
  //   application.deliveries || [],
  //   props.device?.productId || '',
  // );

  const addresses = React.useMemo(
    () => [
      ...(application.addresses || [])
        .filter((address) => address.id)
        .map((address) => ({
          id: address.id || 'default',
          address: `${address.street}, ${address.suburb}, ${address.city}`,
        })),
      ...(props.address
        ? [
            {
              id: props.address.id,
              address: `${props.address.street}, ${props.address.suburb}, ${props.address.city}`,
            },
          ]
        : []),
    ],
    [application.addresses],
  );

  const maxQuantity = React.useMemo(() => {
    const DEFAULT_DELIVERY_ADDRESS_ID = 'default';

    if (!props.device) return 1;

    const remainingDevicesDeliveryAddress = (application.deliveries || []).find(
      (delivery) =>
        delivery.productId === props.device.productId &&
        delivery.cartItemId === props.device.id &&
        delivery.priceType === props.device.priceType &&
        delivery.addressId === DEFAULT_DELIVERY_ADDRESS_ID,
    );

    if (!remainingDevicesDeliveryAddress) {
      return 0;
    }

    if (remainingDevicesDeliveryAddress.quantity === 0) {
      return props.device.quantity || 0;
    }

    if (
      Object.keys(props.deliveryAddress).length &&
      props.deliveryAddress.id !== DEFAULT_DELIVERY_ADDRESS_ID
    ) {
      return remainingDevicesDeliveryAddress.quantity + (currentQuantity ?? 0);
    }

    return remainingDevicesDeliveryAddress.quantity;
  }, [props.device, application.deliveries, props.deliveryAddress, currentQuantity]);

  // exclude virtual devices from the list and devices that are not selected as deliver later
  const devices = application.cart?.filter(
    (item) =>
      !isVirtualDevice(item.title) &&
      application.deliveries?.find((delivery: AppDelivery) => delivery.cartItemId === item.id),
  );

  const productNames = getProductNamesInCart(application.cart || []);

  useDeepCompareEffect(() => {
    // Find the current quantity of devices assigned to a specific address
    const delivery = (application.deliveries ?? []).find(
      (delivery) =>
        delivery.addressId === values.deliveryAddress.id &&
        delivery.cartItemId === values.device.id &&
        delivery.productId === values.device.productId,
    );

    setCurrentQuantity(delivery ? delivery.quantity : 0);
  }, [values.deliveryAddress, application.deliveries]);

  return (
    <article className="flex flex-col md:w-112">
      <h1 className="text-2xl font-medium text-center text-gray-700 md:text-4xl py-7">
        Select the amount of devices and the delivery address
      </h1>
      <section className="flex-1 overflow-y-auto">
        <div className="overflow-y-auto h-full pr-[5px] pl-[5px] flex flex-col gap-6">
          <Field
            name="device"
            type="text"
            items={devices}
            getItemDisplayValue={(item: CartItem) => {
              if (item.priceType === PriceType.MONTHLY) return `${item.title} (Rental)`;

              return item.title;
            }}
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            className="light"
            fieldType="select"
            placeholder="Select a device"
            disabled={props.disableDeviceInput}
          />

          <Field
            name="deliveryAddress"
            type="text"
            items={addresses}
            getItemDisplayValue={(item) => item.address}
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            className="light"
            fieldType="select"
            placeholder="Select address"
            onClick={() => {
              dpoTealiumService?.onDeliveryAddressFieldClick(productNames);
            }}
          />

          <div className="flex justify-center">
            <Field
              name={'quantity'}
              component={SpinEditReskin}
              min={1}
              max={maxQuantity}
              lightMode={true}
              onModified={(value: number) => {
                if (props.quantity < value) {
                  // add
                  dpoTealiumService?.addProductOnAssignAddressModal(
                    productNames,
                    getProductName(values.device.title),
                  );
                } else if (props.quantity > value) {
                  // remove
                  dpoTealiumService?.removeProductOnAssignAddressModal(
                    productNames,
                    getProductName(values.device.title),
                  );
                }
              }}
            />
          </div>
        </div>
      </section>
    </article>
  );
}
